import {
  Grid,
  Typography,
  Button,
  Stack,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { post } from "src/utils/networkUtils";
import Iconify from "../iconify";
import Scoreboard from "./Scoreboard";
import EventsList from "./EventsList";
import GameScore from "./GameScore";

export default function HeadToHeadPlayground({
  userId,
  onUpdateStreak,
  defaultGameMode = null,
}) {
  const [scoreboard, setScoreboard] = useState([]);
  const [events, setEvents] = useState([]);
  const [gameLoading, setGameLoading] = useState(false);
  const [gameMode, setGameMode] = useState(0);
  const [lineupA, setLineupA] = useState([]);
  const [lineupB, setLineupB] = useState([]);
  const [userA, setUserA] = useState(null);
  const [userB, setUserB] = useState(null);

  useEffect(() => {
    if (defaultGameMode !== null) {
      setGameMode(defaultGameMode);
    }
  }, [defaultGameMode]);

  const handleGameModeChange = (event) => {
    setGameMode(event.target.value);
  };

  const handleRunGame = () => {
    setGameLoading(true);
    post(`/admin/versus/users/${userId}/headtohead`, { gameMode })
      .then(function (response) {
        const scoreboard = response.userALineup
          .map((player, index) => {
            if (player.team === null) {
              return null;
            }
            const opponentCard = response.userBLineup[index];
            return {
              playerA: player?.knownname,
              playerB: opponentCard?.knownname,
              teamA: player?.teamExternalRef,
              teamB: opponentCard?.teamExternalRef,
              scoreA: player?.score,
              scoreB: opponentCard?.score,
              position: player?.position,
            };
          })
          .filter((player) => player !== null);
        setScoreboard(scoreboard);
        setEvents(response.events);
        setLineupA(response.userALineup);
        setLineupB(response.userBLineup);
        setUserA(response.userA);
        setUserB(response.userB);
        onUpdateStreak();
      })
      .catch(function (error) {
        console.error("Error fetching versus stats:", error);
      })
      .finally(() => {
        setGameLoading(false);
      });
  };
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} mb={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          {!defaultGameMode ? (
            <FormControl component="fieldset">
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Select Game Mode:
              </Typography>
              <RadioGroup
                row
                aria-label="card-game-mode"
                name="gameMode"
                value={gameMode}
                onChange={handleGameModeChange}
                sx={{
                  "& .MuiFormControlLabel-root": {
                    mr: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    px: 2,
                    py: 0.5,
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  },
                  "& .Mui-checked": {
                    "& + .MuiFormControlLabel-label": {
                      fontWeight: "bold",
                      color: "primary.main",
                    },
                  },
                }}
              >
                <FormControlLabel
                  value="0"
                  control={
                    <Radio
                      sx={{
                        color: "success.main",
                        "&.Mui-checked": { color: "success.main" },
                      }}
                    />
                  }
                  label="Starter"
                  sx={{ bgcolor: "success.lighter", mb: 1 }}
                />
                <FormControlLabel
                  value="1"
                  control={
                    <Radio
                      sx={{
                        color: "warning.dark",
                        "&.Mui-checked": { color: "warning.dark" },
                      }}
                    />
                  }
                  label="Bronze"
                  sx={{ bgcolor: "warning.lighter", mb: 1 }}
                />
                <FormControlLabel
                  value="2"
                  control={
                    <Radio
                      sx={{
                        color: "info.main",
                        "&.Mui-checked": { color: "info.main" },
                      }}
                    />
                  }
                  label="Silver"
                  sx={{ bgcolor: "info.lighter", mb: 1 }}
                />
                <FormControlLabel
                  value="3"
                  control={
                    <Radio
                      sx={{
                        color: "error.main",
                        "&.Mui-checked": { color: "error.main" },
                      }}
                    />
                  }
                  label="Gold"
                  sx={{ bgcolor: "error.lighter", mb: 1 }}
                />
              </RadioGroup>
            </FormControl>
          ) : (
            <Box></Box>
          )}

          <Box>
            <Button
              variant="contained"
              startIcon={
                gameLoading ? null : <Iconify icon="eva:play-circle-outline" />
              }
              onClick={handleRunGame}
              disabled={gameLoading}
              sx={{ minWidth: 200 }}
            >
              {gameLoading ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </Box>
              ) : (
                "Play Head-to-Head Game"
              )}
            </Button>
          </Box>
        </Stack>
      </Grid>
      {gameLoading ? (
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6">Processing game...</Typography>
          </Box>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <GameScore
                lineupA={lineupA}
                lineupB={lineupB}
                userA={userA}
                userB={userB}
              />
              <Scoreboard data={scoreboard} />
            </Grid>
            <Grid item xs={12} md={4}>
              <EventsList events={events} />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
